import React, { useState, useEffect } from 'react';
import '../styles/CookiePolicy.css';
import MainFooter from '../components/MainFooter';

const CookiePolicy = () => {
  const [openSections, setOpenSections] = useState({});
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, can't be disabled
    functional: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already set cookie preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
      setShowBanner(false);
    } else {
      // Show banner after 1 second
      setTimeout(() => setShowBanner(true), 1000);
    }

    const onScroll = () => {
      const doc = document.documentElement;
      setScrollPct((window.scrollY / (doc.scrollHeight - doc.clientHeight)) * 100);
      setShowBackToTop(window.scrollY > 300);
    };
    
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowModal(false);
  };

  const savePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowBanner(false);
    setShowModal(false);
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setCookiePreferences(essentialOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    setShowBanner(false);
    setShowModal(false);
  };

  const sections = [
    {
      id: "what-are-cookies",
      icon: "🍪",
      title: "What Are Cookies?",
      content: (
        <>
          <p>Cookies are small text files that are placed on your computer, smartphone, or other device when you visit our website. They are widely used to make websites work more efficiently, as well as to provide information to the owners of the site.</p>
          <p>Cookies help us provide you with a better experience when you browse our website and allow us to improve our site. They do not typically contain any information that personally identifies you, but personal information that we store about you may be linked to the information stored in and obtained from cookies.</p>
        </>
      )
    },
    {
      id: "how-we-use",
      icon: "🔧",
      title: "How We Use Cookies",
      content: (
        <>
          <p>Eventfork.com uses cookies for the following purposes:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for the website to function properly (e.g., login sessions, shopping cart).</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings to enhance your experience.</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website by collecting anonymous information.</li>
            <li><strong>Marketing Cookies:</strong> Track your browsing habits to deliver personalized advertisements.</li>
          </ul>
        </>
      )
    },
    {
      id: "types-of-cookies",
      icon: "📋",
      title: "Types of Cookies We Use",
      content: (
        <>
          <table>
            <thead>
              <tr><th>Cookie Type</th><th>Purpose</th><th>Duration</th></tr>
            </thead>
            <tbody>
              <tr><td>session_id</td><td>Maintains your login session</td><td>Session</td></tr>
              <tr><td>csrf_token</td><td>Security protection against cross-site request forgery</td><td>Session</td></tr>
              <tr><td>user_preferences</td><td>Stores your language and region preferences</td><td>1 year</td></tr>
              <tr><td>_ga</td><td>Google Analytics - distinguishes unique users</td><td>2 years</td></tr>
              <tr><td>_fbp</td><td>Facebook Pixel - tracks conversions and ads</td><td>3 months</td></tr>
            </tbody>
          </table>
        </>
      )
    },
    {
      id: "third-party",
      icon: "🤝",
      title: "Third-Party Cookies",
      content: (
        <>
          <p>We also use cookies from trusted third-party partners to enhance our services:</p>
          <ul>
            <li><strong>Google Analytics:</strong> Helps us analyze website traffic and user behavior.</li>
            <li><strong>Facebook Pixel:</strong> Enables us to measure the effectiveness of our advertising.</li>
            <li><strong>LinkedIn Insight Tag:</strong> Tracks conversions and retargeting for LinkedIn campaigns.</li>
            <li><strong>Stripe/Razorpay:</strong> Payment processing cookies for secure transactions.</li>
          </ul>
          <p>These third parties have their own cookie policies and data handling practices.</p>
        </>
      )
    },
    {
      id: "managing-cookies",
      icon: "⚙️",
      title: "Managing Your Cookie Preferences",
      content: (
        <>
          <p>You can control and manage cookies in several ways:</p>
          <ul>
            <li><strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies through their settings.</li>
            <li><strong>Our Cookie Manager:</strong> Click the "Cookie Settings" button below to customize your preferences.</li>
            <li><strong>Opt-out Tools:</strong> You can opt out of Google Analytics using the Google Analytics Opt-out Browser Add-on.</li>
          </ul>
          <p>Please note that disabling certain cookies may affect the functionality of our website.</p>
        </>
      )
    },
    {
      id: "data-protection",
      icon: "🔒",
      title: "Data Protection & Your Rights",
      content: (
        <>
          <p>Under data protection laws (including GDPR and India's IT Act), you have the right to:</p>
          <ul>
            <li>Know what personal data we collect through cookies</li>
            <li>Withdraw your consent for non-essential cookies at any time</li>
            <li>Request deletion of your browsing data</li>
            <li>Lodge a complaint with a supervisory authority</li>
          </ul>
          <p>For more information, please review our <a href="/privacy-policy" style={{color: "#0057b8"}}>Privacy Policy</a>.</p>
        </>
      )
    },
    {
      id: "updates",
      icon: "📝",
      title: "Updates to This Policy",
      content: (
        <>
          <p>We may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or our operations. When we make changes, we will update the "Last Updated" date at the top of this page.</p>
          <p>We encourage you to review this Cookie Policy periodically to stay informed about our use of cookies. Your continued use of Eventfork.com after any changes indicates your acceptance of the updated policy.</p>
        </>
      )
    },
    {
      id: "contact",
      icon: "📧",
      title: "Contact Us About Cookies",
      content: (
        <>
          <p>If you have any questions about our use of cookies or this Cookie Policy, please contact us:</p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:privacy@eventfork.com" style={{color: "#0057b8"}}>privacy@eventfork.com</a></li>
            <li><strong>Phone:</strong> +91 123 456 7890</li>
            <li><strong>Address:</strong> Eventfork Technologies, 123 Tech Park, Bengaluru, India - 560001</li>
          </ul>
        </>
      )
    }
  ];

  return (
    <div>

    
    <div style={{ background: "#f4f6f9", minHeight: "100vh" }}>
      
      {/* Progress Bar */}
      <div style={{ 
        position: "fixed", top: 0, left: 0, height: "3px", 
        width: `${scrollPct}%`, background: "#0057b8", 
        zIndex: 1000, transition: "width 0.1s linear" 
      }} />

      {/* Header Section */}
      <div style={{ 
        background: "linear-gradient(135deg, #002f70 0%, #0057b8 55%, #1a7de0 100%)", 
        padding: "44px 48px 38px", 
        position: "relative", 
        overflow: "hidden" 
      }}>
        <div style={{ 
          position: "absolute", right: -80, top: -80, width: 320, height: 320, 
          borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" 
        }} />
        <div style={{ 
          position: "absolute", left: "45%", bottom: -90, width: 240, height: 240, 
          borderRadius: "50%", background: "rgba(255,255,255,0.03)", pointerEvents: "none" 
        }} />
        
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            <span style={{ 
              background: "rgba(255,255,255,0.12)", color: "#c8deff", 
              borderColor: "rgba(255,255,255,0.2)", fontSize: 11, padding: "3px 10px", 
              borderRadius: 20, border: "1px solid" 
            }}>🍪 Cookies Policy</span>
            <span style={{ 
              background: "rgba(255,255,255,0.12)", color: "#c8deff", 
              borderColor: "rgba(255,255,255,0.2)", fontSize: 11, padding: "3px 10px", 
              borderRadius: 20, border: "1px solid" 
            }}>🔒 Privacy</span>
          </div>
          
          <h1 style={{ 
            fontFamily: "'Source Serif 4', serif", 
            fontSize: "clamp(28px, 4vw, 46px)", 
            fontWeight: 700, color: "#fff", marginBottom: 12, lineHeight: 1.12 
          }}>
            Cookie Policy
          </h1>
          
          <p style={{ 
            fontSize: 16, color: "rgba(255,255,255,0.78)", maxWidth: 600, 
            lineHeight: 1.75, fontWeight: 300 
          }}>
            Learn about how Eventfork.com uses cookies to enhance your browsing experience and protect your privacy.
          </p>
          
          <p style={{ 
            fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 16 
          }}>
            Last Updated: April 4, 2026
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={{ background: "#ebeef4", borderBottom: "1px solid #d8dde8" }}>
        <div style={{ 
          maxWidth: 1180, margin: "0 auto", padding: "9px 48px", 
          display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#666" 
        }}>
          <span style={{ color: "#0057b8", cursor: "pointer" }}>Home</span>
          <span>›</span>
          <span style={{ color: "#0057b8", cursor: "pointer" }}>Legal</span>
          <span>›</span>
          <span style={{ color: "#444" }}>Cookie Policy</span>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "40px 48px 80px" }}>
        
        {/* Summary Card */}
        <div className="cp-policy-card cp-summary-card" style={{ marginBottom: 32 }}>
          <div style={{ padding: "24px" }}>
            <h2 style={{ fontFamily: "'Source Serif 4', serif", fontSize: 20, marginBottom: 12 }}>
              Summary of Our Cookie Usage
            </h2>
            <p style={{ fontSize: 14, color: "#555", marginBottom: 16 }}>
              Eventfork.com uses cookies to provide essential functionality, remember your preferences, 
              analyze site traffic, and personalize content. You can manage your cookie preferences at any time.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button 
                onClick={() => setShowModal(true)}
                style={{ padding: "8px 20px", background: "#0057b8", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontFamily: "inherit" }}
              >
                Manage Cookie Settings
              </button>
              <button 
                onClick={acceptAllCookies}
                style={{ padding: "8px 20px", background: "#fff", color: "#0057b8", border: "1px solid #0057b8", borderRadius: 3, cursor: "pointer", fontFamily: "inherit" }}
              >
                Accept All Cookies
              </button>
            </div>
          </div>
        </div>

        {/* Policy Sections */}
        {sections.map(section => (
          <div key={section.id} className="cp-policy-card">
            <div 
              className="cp-policy-header"
              onClick={() => toggleSection(section.id)}
            >
              <h3>
                <span className="cp-policy-icon">{section.icon}</span>
                {section.title}
              </h3>
              <span className={`cp-toggle-icon ${openSections[section.id] ? 'open' : ''}`}>
                ＋
              </span>
            </div>
            {openSections[section.id] && (
              <div className="cp-policy-content">
                <div className="cp-policy-content-inner">
                  {section.content}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Cookie Preferences Table */}
        <div className="cp-policy-card">
          <div 
            className="cp-policy-header"
            onClick={() => toggleSection('cookie-preferences')}
          >
            <h3>
              <span className="cp-policy-icon">⚙️</span>
              Your Current Cookie Preferences
            </h3>
            <span className={`cp-toggle-icon ${openSections['cookie-preferences'] ? 'open' : ''}`}>
              ＋
            </span>
          </div>
          {openSections['cookie-preferences'] && (
            <div className="cp-policy-content">
              <div className="cp-policy-content-inner">
                <table>
                  <thead>
                    <tr><th>Cookie Type</th><th>Status</th><th>Can Disable?</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Necessary Cookies</td>
                      <td>{cookiePreferences.necessary ? '✅ Always Active' : '❌ Disabled'}</td>
                      <td>No (Required for site function)</td>
                    </tr>
                    <tr>
                      <td>Functional Cookies</td>
                      <td>{cookiePreferences.functional ? '✅ Enabled' : '❌ Disabled'}</td>
                      <td>Yes</td>
                    </tr>
                    <tr>
                      <td>Analytics Cookies</td>
                      <td>{cookiePreferences.analytics ? '✅ Enabled' : '❌ Disabled'}</td>
                      <td>Yes</td>
                    </tr>
                    <tr>
                      <td>Marketing Cookies</td>
                      <td>{cookiePreferences.marketing ? '✅ Enabled' : '❌ Disabled'}</td>
                      <td>Yes</td>
                    </tr>
                  </tbody>
                </table>
                <button 
                  onClick={() => setShowModal(true)}
                  style={{ marginTop: 16, padding: "8px 20px", background: "#0057b8", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Change Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cookie Banner */}
      {showBanner && (
        <div className="cp-cookie-banner">
          <p>
            🍪 We use cookies to enhance your experience, analyze site traffic, and personalize content. 
            By clicking "Accept All", you consent to our use of cookies. You can manage your preferences 
            by clicking "Cookie Settings".
          </p>
          <div className="cp-cookie-banner-buttons">
            <button className="cp-btn-cookie-accept" onClick={acceptAllCookies}>
              Accept All
            </button>
            <button className="cp-btn-cookie-settings" onClick={() => setShowModal(true)}>
              Cookie Settings
            </button>
            <button className="cp-btn-cookie-settings" onClick={rejectNonEssential}>
              Reject Non-Essential
            </button>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showModal && (
        <div className="cp-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cp-modal-header">
              <h3>Cookie Preferences</h3>
              <button className="cp-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="cp-modal-body">
              <div className="cp-cookie-option">
                <div className="cp-cookie-option-header">
                  <label>✅ Necessary Cookies</label>
                  <div className="cp-cookie-toggle">
                    <input type="checkbox" checked disabled />
                    <span className="cp-toggle-slider"></span>
                  </div>
                </div>
                <p>Required for the website to function properly. Cannot be disabled.</p>
              </div>

              <div className="cp-cookie-option">
                <div className="cp-cookie-option-header">
                  <label>🎨 Functional Cookies</label>
                  <div className="cp-cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.functional}
                      onChange={(e) => setCookiePreferences({...cookiePreferences, functional: e.target.checked})}
                    />
                    <span className="cp-toggle-slider"></span>
                  </div>
                </div>
                <p>Remember your preferences and settings to enhance your experience.</p>
              </div>

              <div className="cp-cookie-option">
                <div className="cp-cookie-option-header">
                  <label>📊 Analytics Cookies</label>
                  <div className="cp-cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.analytics}
                      onChange={(e) => setCookiePreferences({...cookiePreferences, analytics: e.target.checked})}
                    />
                    <span className="cp-toggle-slider"></span>
                  </div>
                </div>
                <p>Help us understand how visitors interact with our website.</p>
              </div>

              <div className="cp-cookie-option">
                <div className="cp-cookie-option-header">
                  <label>🎯 Marketing Cookies</label>
                  <div className="cp-cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.marketing}
                      onChange={(e) => setCookiePreferences({...cookiePreferences, marketing: e.target.checked})}
                    />
                    <span className="cp-toggle-slider"></span>
                  </div>
                </div>
                <p>Track your browsing habits to deliver personalized advertisements.</p>
              </div>
            </div>
            <div className="cp-modal-footer">
              <button className="cp-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="cp-btn-save" onClick={savePreferences}>Save Preferences</button>
            </div>
          </div>
        </div>
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <button className="cp-back-to-top" onClick={scrollToTop}>
          ↑
        </button>
      )}
    </div>
    <MainFooter />
    </div>
  );
};

export default CookiePolicy;